import React from 'react';
import { useState, useEffect, useRef } from 'react';
import {
    Card,
    FormLayout,
    TextField,
    Checkbox,
    Select,
    Button,
    Stack,
    Popover,
    ActionList,

} from '@shopify/polaris';
import ValueMappingComponent from '../../rules/ValueMappingComponent';
import { ProductsByOptionValueModal } from '../../../../components/ProductsByOptionValueModal';
import { apiFetch } from '../../../../common/Helpers';

export function UpdateProductVariantMetafieldFromVariantOptionMappingSettings(props) {

    const [optionNames, setOptionNames] = useState([]);
    const [loadValuesPopoverActive, setLoadValuesPopoverActive] = useState(false);
    const [showProductsModalActive, setShowProductsModalActive] = useState(false);
    const [showProductsForValue, setShowProductsForValue] = useState("");

    const mappingIdsRef = useRef([]);
    const nextMappingIdRef = useRef(0);

    // Assign stable IDs to any new mappings (covers initial load from backend + adds from API)
    while (mappingIdsRef.current.length < props.settings.valueMappings.length) {
        mappingIdsRef.current.push(nextMappingIdRef.current++);
    }

    const onOptionNameChange = (value) => {
        var newSettings = JSON.parse(JSON.stringify(props.settings));
        newSettings.optionName = value;
        props.onSettingsChange(newSettings);
    }

    const handleMetafieldTypeChange = (value) => {
        var newSettings = JSON.parse(JSON.stringify(props.settings));
        newSettings.metafieldType = value;
        props.onSettingsChange(newSettings);
    }

    const handleMetafieldNamespaceAndKeyChange = (value) => {
        var newSettings = JSON.parse(JSON.stringify(props.settings));
        newSettings.metafieldNamespaceAndKey = value;
        props.onSettingsChange(newSettings);
    }

    const onRemoveMetafieldOnZeroQuantityChange = (value) => {
        var newSettings = JSON.parse(JSON.stringify(props.settings));
        newSettings.removeMetafieldOnZeroQuantity = value;
        props.onSettingsChange(newSettings);
    }

    const handleMappingValueChange = (value, index) => {

        var entries = props.settings.valueMappings;
        entries[index].value = value;

        var newSettings = JSON.parse(JSON.stringify(props.settings));
        newSettings.valueMappings = entries;
        props.onSettingsChange(newSettings);
    }

    const handleMappingKeyChange = (value, index) => {

        var entries = props.settings.valueMappings;
        entries[index].key = value;

        var newSettings = JSON.parse(JSON.stringify(props.settings));
        newSettings.valueMappings = entries;
        props.onSettingsChange(newSettings);
    }

    const handleMappingDelete = (index) => {

        mappingIdsRef.current.splice(index, 1);

        let newArray = [
            ...props.settings.valueMappings.slice(0, index),
            ...props.settings.valueMappings.slice(index + 1)
        ];

        var newSettings = JSON.parse(JSON.stringify(props.settings));
        newSettings.valueMappings = newArray;
        props.onSettingsChange(newSettings);
    }

    const requestValuesUpdate = (newValues) => {
        var newSettings = JSON.parse(JSON.stringify(props.settings));
        newSettings.valueMappings = newValues;
        props.onSettingsChange(newSettings);
    }

    const handleShowProducts = (optionValue) => {
        setShowProductsForValue(optionValue);
        setShowProductsModalActive(true);
    }

    const handleAddNewMapping = () => {

        const newArray = [
            ...props.settings.valueMappings,
            {
                key: "",
                value: ""
            }
        ];

        var newSettings = JSON.parse(JSON.stringify(props.settings));
        newSettings.valueMappings = newArray;
        props.onSettingsChange(newSettings);
    }

    useEffect(() => {

        apiFetch(`api/products/optionNames`, {
            method: "GET",
        }).then(response => response.json())
            .then(data => setOptionNames(data));
    }, []);


    const toggleLoadValuesPopover = () => {
        setLoadValuesPopoverActive(!loadValuesPopoverActive);
    };

    const loadOptionValues = (filter) => {

        toggleLoadValuesPopover();

        apiFetch(`api/products/optionValues?optionName=${encodeURIComponent(props.settings.optionName)}&filter=${filter}`, {
            method: "GET",
        }).then(response => response.json())
            .then(data => {

                var newMappings = data
                    .filter(_ => props.settings.valueMappings.findIndex(__ => __.key === _) === -1)
                    .map(_ => {
                        return {
                            key: _,
                            value: ""
                        };
                    })

                requestValuesUpdate([
                    ...props.settings.valueMappings,
                    ...newMappings
                ]);
            });
    }

    var loadValuesActivator = <Button disclosure size='slim' onClick={toggleLoadValuesPopover}> Load option values</ Button>;
    var loadValuesPopover = <div>
        <Popover
            active={loadValuesPopoverActive}
            activator={loadValuesActivator}
            autofocusTarget="first-node"
            onClose={toggleLoadValuesPopover}
        >
            <ActionList
                actionRole="menuitem"
                items={[
                    { content: 'Use all products', onAction: () => { loadOptionValues("all"); } },
                    { content: 'Skip archived products', onAction: () => { loadOptionValues("skipArchived"); } }]}
            />
        </Popover>
    </div>

    const renderMappings = () => {
        var mappings = props.settings.valueMappings.map((item, index) => {
            var id = mappingIdsRef.current[index];
            return <ValueMappingComponent
                key={"mappingItem" + id}
                id={id}
                index={index}
                mappingKey={item.key}
                mappingValue={item.value}
                onDelete={handleMappingDelete}
                onValueChange={handleMappingValueChange}
                onKeyChange={handleMappingKeyChange}
                onShowProducts={handleShowProducts}
            />
        });

        return <div>
            {mappings}
        </div>
    }

    return (
        <React.Fragment>
            <Card.Section>
                <FormLayout>
                    <Select
                        label="Option Name"
                        placeholder='--- Select Option ---'
                        options={optionNames}
                        onChange={onOptionNameChange}
                        value={props.settings.optionName}
                        error={props.settingsErrors["optionName"]}
                        helpText={
                            <span>
                                Select the option that will be used as value for the metafield.
                            </span>
                        }
                    />
                </FormLayout>
            </Card.Section>
            <Card.Section title="Value Mapping">
                <Stack alignment='center'>
                    <Stack.Item>
                        <p>Map each option value to a metafield value.</p>
                    </Stack.Item>
                    <Stack.Item fill>
                        <p>{loadValuesPopover}</p>
                    </Stack.Item>
                </Stack>
                <div><br />
                    {renderMappings()}
                    <br />
                    <Button plain onClick={handleAddNewMapping}>Add New Mapping</Button>
                </div>
            </Card.Section>
            <Card.Section>
                <FormLayout>
                    <TextField
                        label="Metafield Namespace and key"
                        placeholder="Namespace and key. For example mystore.custom_size"
                        helpText={
                            <span>
                                The namespace and key as they appear in your admin.
                            </span>
                        }
                        value={props.settings.metafieldNamespaceAndKey}
                        error={props.settingsErrors["metafieldNamespaceAndKey"]}
                        onChange={handleMetafieldNamespaceAndKeyChange}
                        autoComplete="off"
                    />
                    <Select
                        label="Metafield Type"
                        options={[
                            {
                                'label': 'Single line text',
                                'value': 'single_line_text_field'
                            },
                            {
                                'label': 'Multi line text',
                                'value': 'multi_line_text_field'
                            },
                            {
                                'label': 'List (single line text)',
                                'value': 'list.single_line_text_field'
                            }
                        ]}
                        onChange={handleMetafieldTypeChange}
                        value={props.settings.metafieldType}
                        helpText={
                            <span>
                                The type of the metafield that will be populated.
                            </span>
                        }
                    />
                    <Checkbox
                        label="Remove on zero quantity"
                        checked={props.settings.removeMetafieldOnZeroQuantity}
                        onChange={onRemoveMetafieldOnZeroQuantityChange}
                        helpText={
                            <span>
                                Metafield will not be set or will be removed when the respective variant has zero quantity.
                            </span>
                        }
                    />
                </FormLayout>
            </Card.Section>
            <ProductsByOptionValueModal
                active={showProductsModalActive}
                onClose={() => setShowProductsModalActive(false)}
                optionName={props.settings.optionName}
                optionValue={showProductsForValue}
            />
        </React.Fragment>
    );
}